import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import PasswordInput from "@/components/app/PasswordInput";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import StyledFormModal from "@/components/globalModal/StyledModal";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useSetCosignPin } from "@/queries/use-set-cosign-pin";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";

interface SetCosignPinModalProps {
  open: boolean;
  onClose: () => void;
  /** "reset" reuses the same endpoint to overwrite an existing PIN. */
  mode?: "set" | "reset";
}

const COSIGN_PIN_MIN_LENGTH = 4;

const setCosignPinValidationSchema = Yup.object({
  pin: Yup.string()
    .trim()
    .required("Co-Sign PIN is required.")
    .min(COSIGN_PIN_MIN_LENGTH, "Minimum 4 characters required."),
  confirmPin: Yup.string()
    .trim()
    .required("Confirm Co-Sign PIN is required.")
    .oneOf([Yup.ref("pin")], "New and Confirm Co-Sign PIN do not match."),
});

const SetCosignPinModal = ({
  open,
  onClose,
  mode = "set",
}: SetCosignPinModalProps) => {
  const formik = useFormik({
    initialValues: { pin: "", confirmPin: "" },
    validationSchema: setCosignPinValidationSchema,
    onSubmit,
  });
  const { mutateAsync: setCosignPin } = useSetCosignPin();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  function closeAndReset() {
    formik.resetForm();
    onClose();
  }

  function onSubmit(values: { pin: string; confirmPin: string }) {
    return setCosignPin({
      pin: values.pin.trim(),
      confirmPin: values.confirmPin.trim(),
      xTENANTID: getXtenantId() ?? undefined,
    })
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({ queryKey: ["provider-profile"] });
        closeAndReset();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  }

  return (
    <StyledFormModal
      open={open}
      onClose={closeAndReset}
      dialogTitle={mode === "reset" ? "Reset Co-Sign PIN" : "Set Co-Sign PIN"}
      dialogAction={
        <Flex justifyContent={"end"} gap={1}>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={closeAndReset}
          />
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={formik.submitForm}
            sx={{ width: "100px" }}
            disabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          height: "auto",
          width: "400px",
        },
      }}
    >
      <Box sx={{ padding: "20px 0" }}>
        <Flex flexDirection={"column"} gap={"18px"}>
          <Grid container spacing={2.5} direction={"column"}>
            <Grid item xs={12}>
              <PasswordInput
                name="pin"
                onBlur={formik.handleBlur}
                label={"New Co-Sign PIN"}
                value={formik.values.pin}
                onChange={(value) => formik.setFieldValue("pin", value)}
                required
                error={formik.touched.pin && formik.errors.pin}
              />
            </Grid>
            <Grid item xs={12}>
              <PasswordInput
                name="confirmPin"
                onBlur={formik.handleBlur}
                label={"Confirm New Co-Sign PIN"}
                value={formik.values.confirmPin}
                onChange={(value) => formik.setFieldValue("confirmPin", value)}
                required
                error={formik.touched.confirmPin && formik.errors.confirmPin}
              />
            </Grid>
          </Grid>
          <Text color="#1A1A1A80">Minimum 4 characters.</Text>
        </Flex>
      </Box>
    </StyledFormModal>
  );
};

export default SetCosignPinModal;
